import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { AuthHeading, AuthPrimaryButton, AuthShell } from './AuthShell';
import { AuthErrorBanner } from './fieldError';
import { maskEmail, maskPhone } from './mask';
import { OtpInput } from './OtpInput';
import { RESEND_COOLDOWN_SECONDS } from './resendCooldown';
import type { ReactNode } from 'react';
import type { useAuthActions } from '../hooks/useAuthActions';

const FONT_SANS = 'var(--font-brand)';

/**
 * Second-factor prompt shown after a correct password.
 *
 * Deliberately offers no way to skip — the account is already protected at
 * this point, and an escape hatch here would undo that.
 *
 * The copy names the actual method. "Enter your code" is useless when the user
 * has to know whether to open an app, unlock their phone, or check email.
 */
/**
 * What each factor's screen says, and why the three differ.
 *
 * The body NAMES WHERE THE CODE WENT, masked, rather than describing the
 * channel. "We emailed you a 6-digit code" is true of every email code ever
 * sent; "sent to 73n***@gmeenramy.com" tells the user which inbox to open,
 * which is the only question this screen leaves them with. The enrolment
 * screen has said it that way for a while and this one had not.
 *
 * No expiry line. It said "It expires in N minutes" on the email factor alone
 * — a whole sentence for a deadline nobody is still on the screen to hit, when
 * what actually goes wrong is the code not arriving. "Resend code" answers
 * that directly now.
 */
const CHALLENGE_COPY = {
  phone: {
    title: 'Check your phone',
    body: (target?: string) =>
      target
        ? `Enter the 6-digit code sent to ${maskPhone(target)}.`
        : 'Enter the 6-digit code we texted you.',
  },
  totp: {
    // Nothing was sent, so "check" would be wrong: the code is already on the
    // device, changing every thirty seconds. There is no target to name for
    // the same reason.
    title: 'Open your authenticator',
    body: () => 'Enter the current 6-digit code for BIASafe.',
  },
  email: {
    title: 'Check your email',
    body: (target?: string) =>
      target
        ? `Enter the 6-digit code sent to ${maskEmail(target)}.`
        : 'Enter the 6-digit code we emailed you.',
  },
} as const;

const MFA_LOGIN_RECAPTCHA_ID = 'mfa-login-recaptcha';

export function MfaChallenge({
  kind,
  hero,
  label,
  target,
  smsSent,
  smsFailed = false,
  alternatives,
  onSwitch,
  error,
  isSubmitting,
  onSendSms,
  onResend,
  onSubmit,
  onCancel,
}: {
  hero?: ReactNode;
  kind: 'phone' | 'email' | 'totp';
  label?: string;
  /** Where the code went, unmasked. The copy masks it. */
  target?: string;
  smsSent: boolean;
  /** The text could not be sent: Resend must be live, not on its cooldown. */
  smsFailed?: boolean;
  /** Other enrolled factors the person can switch to. */
  alternatives?: Array<{
    kind: 'phone' | 'totp';
    factorUid: string;
    label?: string;
  }>;
  onSwitch?: (factorUid: string) => void;
  error: string | null;
  isSubmitting: boolean;
  onSendSms: (containerId: string) => void;
  onResend: (containerId: string) => void;
  onSubmit: (code: string) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState('');
  // Bumped only by a FAILED attempt, and used as the OtpInput key.
  const [attempt, setAttempt] = useState(0);
  const wasSubmitting = useRef(false);
  // Seconds before "Resend code" is live. Same rule as the enrolment step: a
  // resend pressed on reflex costs a message and, on the phone factor, spends
  // the verificationId the code in the user's hand belongs to.
  const [resendIn, setResendIn] = useState(RESEND_COOLDOWN_SECONDS);
  const [resendCount, setResendCount] = useState(0);

  // Firebase needs an explicit "send" for SMS before a code exists to type.
  // Fired once on mount; `smsSent` guards the resend.
  useEffect(() => {
    if (kind === 'phone' && !smsSent) onSendSms(MFA_LOGIN_RECAPTCHA_ID);
  }, [kind, smsSent, onSendSms]);

  // Runs down to zero, re-armed by each resend so the wait is a limit rather
  // than one press of delay. TOTP never shows the button, so it never matters
  // there. Interval, not a timestamp: the number is on screen and has to tick.
  useEffect(() => {
    if (kind === 'totp') return;
    setResendIn(RESEND_COOLDOWN_SECONDS);
    const id = window.setInterval(() => {
      setResendIn((n) => {
        if (n <= 1) {
          window.clearInterval(id);
          return 0;
        }
        return n - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [kind, resendCount]);

  /**
   * Empty the boxes when a code comes back wrong.
   *
   * A rejected code left all six digits sitting there, so the next attempt
   * started with six wrong characters to delete one at a time — and the screen
   * said "check it and try again" above a field that still held the thing that
   * had just failed. OtpInput keeps its digits internally, so the only way to
   * clear them from out here is to remount it; MfaEnrollment already uses the
   * same key trick when going Back.
   *
   * Keyed on a FAILED attempt, not on every completion. Keying on completion
   * would blank the digits the instant the sixth one landed, while the request
   * was still in flight — the user would watch their code vanish and not know
   * whether it had been sent. Watching `isSubmitting` fall back to false with
   * an error set is what identifies a failure, and it works for a repeat of the
   * SAME error message, which comparing the error string would not.
   */
  useEffect(() => {
    if (wasSubmitting.current && !isSubmitting && error) {
      setAttempt((n) => n + 1);
    }
    wasSubmitting.current = isSubmitting;
  }, [isSubmitting, error]);

  const copy = CHALLENGE_COPY[kind];
  const body = copy.body(target);
  // TOTP has no send step — the code is generated on the device — so it is
  // never "waiting", and the effect above only fires for phone.
  const waitingForSms = kind === 'phone' && !smsSent;

  return (
    // The shared shell, like the rest of this flow. This screen carried a
    // fourth hand-rolled copy of it — 28px heading, `max-w-[420px]`, purple
    // primary — so the challenge you hit when SIGNING IN looked nothing like
    // the enrolment screens that set the factor up.
    <AuthShell dataName="MfaChallenge" hero={hero}>
      <form
        className="w-full"
        style={{ fontFamily: FONT_SANS }}
        onSubmit={(e) => {
          // Enter still works, but only on a complete code -- there is no
          // button to press, so this is the only way in besides onComplete.
          e.preventDefault();
          if (code.length === 6 && !isSubmitting) onSubmit(code);
        }}
      >
        <AuthHeading title={copy.title}>
          {body}
          {label ? ` (${label})` : ''}
        </AuthHeading>

        {error && <AuthErrorBanner className="mt-4">{error}</AuthErrorBanner>}

        {/* One box per digit (auto-advance / paste), submits when full. */}
        <div className="mt-6">
          <OtpInput
            key={attempt}
            onChange={setCode}
            onComplete={(c) => {
              if (!isSubmitting && !waitingForSms) onSubmit(c);
            }}
            disabled={waitingForSms}
            autoFocus
            withSeparator
          />
        </div>

        {waitingForSms && (
          <p className="mt-3 text-[13px] text-[#717182]">Sending your code…</p>
        )}

        {/* No submit button. The sixth digit sends the code -- by typing or by
            paste, both of which go through OtpInput's commit and fire
            onComplete -- so a button could only ever be pressed after the
            request it claims to trigger had already gone. It read as the thing
            that submits the form while being the one control that never did.
            MfaEnrollment dropped it for the same reason; this screen kept it.

            The wait is shown instead, which is what the button was really
            carrying. */}
        {isSubmitting && (
          <p className="mt-6 flex items-center justify-center gap-2 text-[14px] text-[#717182]">
            <Loader2 className="h-4 w-4 animate-spin" />
            Verifying…
          </p>
        )}

        {/* Cancel and Resend, the pair the enrolment code step uses.
            
            "Use a different account" was the only thing on offer, and it is
            not what a stuck user needs: the commonest failure here is a code
            that never arrived, and leaving is the answer to a different
            question.
            
            No resend on TOTP — the code is generated on the device, so the
            button would be one that cannot do anything. There Cancel stays on
            its own. */}
        {kind === 'totp' ? (
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={onCancel}
              className="text-[14px] text-[#3e3e3e] hover:underline"
            >
              Use a different account
            </button>
          </div>
        ) : (
          <div className="mt-6 flex gap-4 w-full">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-[#f5f7fa] relative rounded-lg hover:bg-[#e8ebf0] transition-colors cursor-pointer"
            >
              <div className="flex flex-row items-center justify-center size-full">
                <div className="box-border content-stretch flex gap-[10px] items-center justify-center px-[16px] py-[8px] relative w-full">
                  <p className="leading-[24px] text-[16px] text-[#121212] font-medium">
                    Cancel
                  </p>
                </div>
              </div>
            </button>
            <div className="flex-1">
              <AuthPrimaryButton
                disabled={isSubmitting || (resendIn > 0 && !smsFailed)}
                onClick={() => {
                  onResend(MFA_LOGIN_RECAPTCHA_ID);
                  setResendCount((n) => n + 1);
                }}
              >
                {resendIn > 0 && !smsFailed
                  ? `Resend code in ${resendIn}s`
                  : 'Resend code'}
              </AuthPrimaryButton>
            </div>
          </div>
        )}

        {/* A way out of a factor that cannot be used, and of the gate itself.
            Without these a lost phone or a wiped authenticator ended here with
            no next step. The links are plain text under the existing controls;
            nothing about the screen's layout changes. */}
        <div className="mt-6 flex flex-col items-center gap-2 text-[13px] text-[#717182]">
          {alternatives?.map((alt) => (
            <button
              key={alt.factorUid}
              type="button"
              onClick={() => onSwitch?.(alt.factorUid)}
              className="font-medium text-[#121212] hover:underline"
            >
              {alt.kind === 'totp'
                ? 'Use your authenticator app instead'
                : 'Use text message instead'}
            </button>
          ))}
          <p>
            Cannot use your code?{' '}
            <a
              className="underline"
              href="mailto:support@biasafe.ai?subject=Cannot%20complete%20two-factor%20sign-in"
            >
              Contact support
            </a>
          </p>
        </div>

        {/* reCAPTCHA host for the SMS send. See MfaCard for why it is
            always mounted rather than conditionally rendered. */}
        <div id={MFA_LOGIN_RECAPTCHA_ID} />
      </form>
    </AuthShell>
  );
}

/**
 * The second-factor screen for whatever challenge `useAuthActions` is holding.
 *
 * Shared by the log-in and sign-up pages: a Google or Microsoft sign-in can
 * stop for a second factor on either, and the challenge state lives in the
 * hook, so each page renders this in place of its form. Renders nothing when
 * there is no challenge.
 */
export function ActiveMfaChallenge({
  actions,
  hero,
}: {
  actions: ReturnType<typeof useAuthActions>;
  hero?: ReactNode;
}) {
  const c = actions.mfaChallenge;
  if (!c) return null;
  return (
    <MfaChallenge
      hero={hero}
      kind={c.kind}
      label={c.label}
      target={c.target}
      smsSent={Boolean(c.verificationId)}
      smsFailed={Boolean(c.smsFailed)}
      alternatives={c.alternatives}
      onSwitch={actions.switchMfaFactor}
      error={actions.error}
      isSubmitting={actions.isLoading}
      onSendSms={actions.sendMfaSms}
      onResend={actions.resendMfaCode}
      onSubmit={actions.submitMfaCode}
      onCancel={actions.cancelMfaChallenge}
    />
  );
}
